import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowLeft, Building2, Armchair, LayoutGrid, Info } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { createVenue } from "@/lib/venues.functions";
import { getAuthHeaders } from "@/lib/auth-helper";
import { AccessGate } from "@/components/permissions/AccessGate";

export const Route = createFileRoute("/dashboard/venues/new")({
  head: () => ({ meta: [{ title: "Nuevo venue — ZYNTRA" }] }),
  component: NewVenueGuarded,
});

function NewVenueGuarded() {
  return (
    <AccessGate required="manage-venues">
      <NewVenuePage />
    </AccessGate>
  );
}

function NewVenuePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [isReusable, setIsReusable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [user, loading]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const result = await createVenue({
        data: { name, city, address: address || undefined, isReusable },
        headers,
      } as any);
      navigate({ to: "/dashboard/venues/$venueId", params: { venueId: result.venueId } });
    } catch (e: any) {
      setError(e.message || "Error al crear venue");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pt-24 pb-20 sm:px-6">
      <Link to="/dashboard/venues" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Volver a venues
      </Link>

      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl gradient-primary">
          <Building2 className="h-6 w-6 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-foreground">Nuevo venue</h1>
          <p className="text-sm text-muted-foreground">Definí los datos básicos. Después agregás las zonas.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-border bg-card p-6">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Nombre del venue *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            placeholder="Estadio Único, Teatro Gran Rex..."
            className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Ciudad *</label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
              maxLength={80}
              placeholder="Buenos Aires"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Dirección</label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              maxLength={200}
              placeholder="Av. Corrientes 857"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={isReusable}
            onChange={(e) => setIsReusable(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-primary"
          />
          Reutilizable en otros eventos
        </label>

        {/* Hint claro: el venue admite ambos tipos de zonas */}
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-primary">
            <Info className="h-3.5 w-3.5" />
            Después de crear el venue
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            Vas a poder dibujar zonas en el plano. Cada zona puede ser de uno de estos tipos:
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-bold text-foreground">
                <LayoutGrid className="h-3.5 w-3.5 text-primary" /> General
              </div>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Zona por capacidad (ej: Campo, Pullman). El comprador elige cantidad.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-bold text-foreground">
                <Armchair className="h-3.5 w-3.5 text-primary" /> Numerada
              </div>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Asientos con fila y número (ej: Platea A1, A2). El comprador elige cada butaca.
              </p>
            </div>
          </div>
        </div>

        {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-xl gradient-primary py-3 text-sm font-bold text-primary-foreground glow-primary disabled:opacity-50"
        >
          {submitting ? "Creando..." : "Crear venue"}
        </button>
      </form>
    </div>
  );
}
